import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Link,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TablePagination,
  IconButton,
  Tooltip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { useParams } from "react-router-dom";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DownloadIcon from '@mui/icons-material/Download';
import StorageIcon from '@mui/icons-material/Storage';
// import ArchitectureIcon from '@mui/icons-material/Architecture';

const MemoryLeakDetails = () => {
  const [heapSummary, setHeapSummary] = useState(null);
  const [leakSummary, setLeakSummary] = useState(null);
  const [filesLeakage, setFilesLeakage] = useState([]);
  const [staticAnalysis, setStaticAnalysis] = useState(null);
  const [unsafeFunctions, setUnsafeFunctions] = useState(null);
  const [dynamicAnalysis, setDynamicAnalysis] = useState(null);
  const [stackUsage, setStackUsage] = useState(null);
  const [securityScore, setSecurityScore] = useState(null);
  const [reportFiles, setReportFiles] = useState({});
  const [analysisMetadata, setAnalysisMetadata] = useState(null);
  const [allRunsData, setAllRunsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTool, setActiveTool] = useState(null);
  const [runCount, setRunCount] = useState(null);
  const [memoryResults, setMemoryResults] = useState([]);
  const [customRunCount, setCustomRunCount] = useState("");
  const [customInputValue, setCustomInputValue] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateMessage, setUpdateMessage] = useState({ type: '', text: '' });
  const [customError, setCustomError] = useState("");
  
  // File upload state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState({ type: '', text: '' });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentRunId, setCurrentRunId] = useState("");
  
  // ELF Binary Upload Response state
  const [elfBinaryDetails, setElfBinaryDetails] = useState(null);
  const [elfLoading, setElfLoading] = useState(false);
  
  // MCU Core Selection state
  const [mcuCore, setMcuCore] = useState("");
  const [mcuCoreDialogOpen, setMcuCoreDialogOpen] = useState(false);
  const [mcuCoreLoading, setMcuCoreLoading] = useState(false);
  const [mcuCoreMessage, setMcuCoreMessage] = useState({ type: '', text: '' });
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { projectId } = useParams();

  const runCountOptions = [10, 20, 30, 40, 50, 60, 80, 100, 150, 200, 250, 300, 400];

  // MCU Core options - you can expand this list based on your supported cores
  const mcuCoreOptions = [
    "cortex-m0",
    // "cortex-m0plus", 
    "cortex-m3",
    "cortex-m4",
    //"cortex-m7",
    "cortex-m23",
    "cortex-m33",
    "v8-m"
    //"cortex-m55",
    // "cortex-a5",
    // "cortex-a7",
    // "cortex-a8",
    // "cortex-a9",
    // "cortex-a15",
    // "cortex-a53",
    // "cortex-a57",
    // "cortex-a72",
    // "cortex-a76",
    // "riscv32",
    // "riscv64"
  ];

  useEffect(() => {
    const fetchMemoryLeakData = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const response = await fetch(
          `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/reports/valgrind-memleak-result?project_id=${projectId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch data");
        }

        const data = await response.json();
        processMemoryLeakData(data);
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching memory leak data:", error);
        setLoading(false);
      }
    };

    if (projectId) {
      fetchMemoryLeakData();
    }
  }, [projectId]);

  // Fetch ELF Binary Upload Details
  const fetchElfBinaryDetails = async () => {
    if (!projectId) return;
    
    setElfLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/reports/renode-binary-file-upload?project_id=${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setElfBinaryDetails(data);
        
        // Set current MCU core from the response if available
        if (data.renodeBinaryPaths?.mcu_core) {
          setMcuCore(data.renodeBinaryPaths.mcu_core);
        }
      } else if (response.status === 404) {
        // No ELF binary uploaded yet, which is fine
        setElfBinaryDetails(null);
      } else {
        throw new Error("Failed to fetch ELF binary details");
      }
    } catch (error) {
      console.error("Error fetching ELF binary details:", error);
      setElfBinaryDetails(null);
    } finally {
      setElfLoading(false);
    }
  };

  useEffect(() => {
    if (activeTool === "renode" && projectId) {
      fetchElfBinaryDetails();
    }
  }, [activeTool, projectId]);

  const processMemoryLeakData = (data) => {
    if (data.renodeMemLeakResult && data.renodeMemLeakResult.length > 0) {
      setActiveTool("renode");
      processRenodeData(data.renodeMemLeakResult);
    } else if (data.valgrindMemLeakResult && data.valgrindMemLeakResult.length > 0) {
      setActiveTool("valgrind");
      processValgrindData(data.valgrindMemLeakResult);
    } else {
      setActiveTool(null);
    }
  };

  const processValgrindData = (memoryResults) => {
    if (memoryResults.length > 0) {
      const sortedResults = memoryResults
        .map((result) => ({
          ...result,
          localMemoryTestDate: new Date(result.memoryTestDate * 1000),
        }))
        .sort((a, b) => b.localMemoryTestDate - a.localMemoryTestDate);

      const latestResult = sortedResults[0];
      const memoryTestResult = latestResult.memoryTestResult || {};

      setHeapSummary(memoryTestResult.heapSummary || {});
      setLeakSummary(memoryTestResult.leakSummary || {});
      setFilesLeakage(memoryTestResult.filesLeakage || []);
      setRunCount(latestResult.run_count || null);
      setMemoryResults(sortedResults);
      
      // Clear Renode-specific data
      setStaticAnalysis(null);
      setUnsafeFunctions(null);
      setDynamicAnalysis(null);
      setStackUsage(null);
      setSecurityScore(null);
      setReportFiles({});
      setAnalysisMetadata(null);
      setAllRunsData([]);
      setElfBinaryDetails(null);
      setMcuCore("");
    }
  };

  const processRenodeData = (memoryResults) => {
    if (memoryResults.length > 0) {
      const sortedResults = memoryResults
        .map((result) => ({
          ...result,
          localMemoryTestDate: new Date(result.memoryTestDate * 1000),
        }))
        .sort((a, b) => b.localMemoryTestDate - a.localMemoryTestDate);

      const latestResult = sortedResults[0];
      const memoryTestResult = latestResult.memoryTestResult || {};

      // Set all available data
      setHeapSummary(memoryTestResult.heapSummary || {});
      setLeakSummary(memoryTestResult.leakSummary || {});
      setFilesLeakage(memoryTestResult.filesLeakage || []);
      setStaticAnalysis(memoryTestResult.staticAnalysis || {});
      setUnsafeFunctions(memoryTestResult.unsafeFunctions || {});
      setDynamicAnalysis(memoryTestResult.dynamicAnalysis || {});
      setStackUsage(memoryTestResult.stackUsage || null);
      setSecurityScore(memoryTestResult.securityScore || null);
      setReportFiles(memoryTestResult.reportFiles || {});
      setAnalysisMetadata(latestResult.analysisMetadata || null);
      setAllRunsData(latestResult.all_runs_data || []);
      setRunCount(latestResult.run_count || null);
      setMemoryResults(sortedResults);
      
      // Set current run ID for file upload
      if (sortedResults[0]?.run_id) {
        setCurrentRunId(sortedResults[0].run_id);
      }
    }
  };

  // Handle MCU Core selection
  const handleMcuCoreChange = (event) => {
    setMcuCore(event.target.value);
  };

  // Handle MCU Core update
  const handleMcuCoreUpdate = async () => {
    if (!mcuCore) {
      setMcuCoreMessage({ type: 'error', text: 'Please select an MCU Core' });
      return;
    }

    setMcuCoreLoading(true);
    setMcuCoreMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/reports/renode-binary-core-select`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            project_id: projectId,
            mcu_core: mcuCore
          }),
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setMcuCoreMessage({ 
          type: 'success', 
          text: `MCU Core updated successfully to ${mcuCore}` 
        });
        
        // Refresh ELF binary details to get updated MCU core
        fetchElfBinaryDetails();
        
        // Close dialog after success
        setTimeout(() => {
          setMcuCoreDialogOpen(false);
        }, 2000);
      } else {
        setMcuCoreMessage({ 
          type: 'error', 
          text: result.error || 'Failed to update MCU Core' 
        });
      }
    } catch (error) {
      console.error('Error updating MCU Core:', error);
      setMcuCoreMessage({ 
        type: 'error', 
        text: 'Failed to update MCU Core. Please try again.' 
      });
    } finally {
      setMcuCoreLoading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.elf')) {
        setUploadMessage({ type: 'error', text: 'Only .elf files are allowed' });
        return;
      }
      
      // Validate file size (100MB limit)
      if (file.size > 100 * 1024 * 1024) {
        setUploadMessage({ type: 'error', text: 'File size too large. Maximum size is 100MB' });
        return;
      }
      
      setSelectedFile(file);
      setUploadMessage({ type: '', text: '' });
    }
  };

  // Handle file upload
  const handleFileUpload = async () => {
    if (!selectedFile || !currentRunId) {
      setUploadMessage({ type: 'error', text: 'Please select a file and ensure run ID is available' });
      return;
    }

    setUploadLoading(true);
    setUploadProgress(0);
    setUploadMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem("access_token");
      
      // Convert file to base64
      const reader = new FileReader();
      
      reader.onload = async (event) => {
        try {
          const base64Content = event.target.result.split(',')[1]; // Remove data URL prefix
          
          // Simulate progress
          const progressInterval = setInterval(() => {
            setUploadProgress(prev => {
              if (prev >= 90) {
                clearInterval(progressInterval);
                return prev;
              }
              return prev + 10;
            });
          }, 200);

          // Send as JSON body instead of FormData
          const response = await fetch(
            `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/reports/renode-binary-file-upload`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                project_id: projectId,
                run_id: currentRunId,
                file_name: selectedFile.name,
                file_content: base64Content
              }),
            }
          );

          clearInterval(progressInterval);
          setUploadProgress(100);

          const result = await response.json();

          if (response.ok) {
            setUploadMessage({ 
              type: 'success', 
              text: result.message || `Binary file "${selectedFile.name}" uploaded successfully!` 
            });
            
            // Refresh ELF binary details after successful upload
            fetchElfBinaryDetails();
            
            // Close dialog after success
            setTimeout(() => {
              setUploadDialogOpen(false);
              setSelectedFile(null);
              setUploadProgress(0);
              setUploadMessage({ type: '', text: '' });
            }, 2000);
          } else {
            setUploadMessage({ 
              type: 'error', 
              text: result.error || result.message || 'Failed to upload file' 
            });
            setUploadProgress(0);
          }
        } catch (error) {
          console.error('Error processing file upload:', error);
          setUploadMessage({ 
            type: 'error', 
            text: 'Failed to process file upload. Please try again.' 
          });
          setUploadProgress(0);
        } finally {
          setUploadLoading(false);
        }
      };

      reader.onerror = () => {
        setUploadMessage({ 
          type: 'error', 
          text: 'Failed to read file. Please try again.' 
        });
        setUploadLoading(false);
        setUploadProgress(0);
      };

      // Read file as base64
      reader.readAsDataURL(selectedFile);

    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadMessage({ 
        type: 'error', 
        text: 'Failed to upload file. Please try again.' 
      });
      setUploadProgress(0);
      setUploadLoading(false);
    }
  };

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format date for display
  const formatUploadDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    try {
      return new Date(timestamp * 1000).toLocaleString();
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Handle download from S3
  const handleDownload = (s3Path) => {
    if (s3Path) {
      window.open(s3Path, '_blank');
    }
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle custom input change with validation
  const handleCustomInputChange = (e) => {
    const value = e.target.value;
    setCustomInputValue(value);
    setCustomError("");

    if (value === "") {
      setCustomRunCount("");
      return;
    }

    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 1) {
      setCustomError("Please enter a valid number greater than 0");
      setCustomRunCount("");
    } else if (numValue > 1000) {
      setCustomError("Value cannot exceed 1000");
      setCustomRunCount("");
    } else {
      setCustomRunCount(numValue.toString());
      setCustomError("");
    }
  };

  // Handle dropdown change
  const handleRunCountChange = (e) => {
    const value = e.target.value;
    if (value === "custom") {
      setCustomInputValue("");
      setCustomRunCount("");
      setCustomError("");
    } else {
      setCustomRunCount(value);
      setCustomInputValue("");
      setCustomError("");
    }
  };

  const handleRunCountUpdate = async () => {
    if (!customRunCount) {
      setUpdateMessage({ type: 'error', text: 'Please select or enter a run count value' });
      return;
    }

    const numValue = parseInt(customRunCount);
    if (isNaN(numValue) || numValue < 1 || numValue > 1000) {
      setUpdateMessage({ type: 'error', text: 'Please enter a valid run count between 1 and 1000' });
      return;
    }

    setUpdateLoading(true);
    setUpdateMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/reports/renode-run-count-update-result`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            project_id: projectId,
            run_count: numValue
          }),
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setUpdateMessage({ 
          type: 'success', 
          text: `Run count updated successfully to ${numValue}. Analysis will restart with new configuration.` 
        });
        setRunCount(numValue);
        setDialogOpen(false);
        setCustomRunCount('');
        setCustomInputValue('');
        
        // Refresh data after a short delay
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setUpdateMessage({ 
          type: 'error', 
          text: result.error || 'Failed to update run count' 
        });
      }
    } catch (error) {
      console.error('Error updating run count:', error);
      setUpdateMessage({ 
        type: 'error', 
        text: 'Failed to update run count. Please try again.' 
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0 || bytes === null || bytes === undefined) return "0 B";
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getSecurityScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!activeTool) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="textSecondary">
          No memory leak data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Tool Indicator with Run Count and Update Button */}
      <Paper elevation={1} style={{ marginBottom: "16px", padding: "16px" }}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <Typography variant="h6" color="primary">
                  {activeTool === "renode" ? "Dynamic Memory Analysis" : "Static Memory Analysis"}
                </Typography>
              </Grid>
              {runCount !== null && (
                <Grid item>
                  <Chip 
                    label={`Current Run Count: ${runCount}`} 
                    color="primary" 
                    variant="outlined"
                  />
                </Grid>
              )}
              {mcuCore && (
                <Grid item>
                  <Chip 
                    label={`MCU Core: ${mcuCore}`} 
                    color="secondary" 
                    variant="outlined"
                    // icon={<ArchitectureIcon />}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          {activeTool === "renode" && (
            <Grid item>
              <Grid container spacing={1}>
                {/* MCU Core Dropdown Button - ADDED THIS */}
                <Grid item>
                  <Button 
                    variant="outlined" 
                    color="secondary"
                    // startIcon={<ArchitectureIcon />}
                    onClick={() => setMcuCoreDialogOpen(true)}
                  >
                    MCU Core
                  </Button>
                </Grid>
                <Grid item>
                  <Button 
                    variant="outlined" 
                    color="primary"
                    startIcon={<UploadFileIcon />}
                    onClick={() => setUploadDialogOpen(true)}
                  >
                    Upload Binary File
                  </Button>
                </Grid>
                <Grid item>
                  <Button 
                    variant="contained" 
                    color="primary"
                    onClick={() => setDialogOpen(true)}
                  >
                    Update Run Count
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Update Message */}
      {updateMessage.text && (
        <Alert 
          severity={updateMessage.type} 
          sx={{ mb: 2 }}
          onClose={() => setUpdateMessage({ type: '', text: '' })}
        >
          {updateMessage.text}
        </Alert>
      )}

      {/* ELF Binary Upload Details Section */}
      {activeTool === "renode" && (
        <Grid item xs={12} sx={{ mb: 3 }}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h5" component="h2">
                Binary File Details
              </Typography>
            </Box>
            
            {elfLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <CircularProgress />
              </Box>
            ) : elfBinaryDetails ? (
              <Card variant="outlined">
                <CardContent>
                  <Grid container spacing={3}>
                    {/* File Information */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom color="primary">
                        File Information
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableBody>
                            <TableRow>
                              <TableCell><strong>File Name</strong></TableCell>
                              <TableCell>
                                <Box display="flex" alignItems="center">
                                  <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
                                  {elfBinaryDetails.renodeBinaryPaths?.file_name || 'N/A'}
                                </Box>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell><strong>File Size</strong></TableCell>
                              <TableCell>
                                {formatFileSize(elfBinaryDetails.renodeBinaryPaths?.file_size_bytes)}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell><strong>Upload Date</strong></TableCell>
                              <TableCell>
                                {formatUploadDate(elfBinaryDetails.renodeBinaryPaths?.upload_timestamp)}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell><strong>MCU Core</strong></TableCell>
                              <TableCell>
                                <Box display="flex" alignItems="center">
                                  {/* <ArchitectureIcon sx={{ mr: 1, color: 'secondary.main' }} /> */}
                                  {elfBinaryDetails.renodeBinaryPaths?.mcu_core || 'Not Selected'}
                                </Box>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>

                    {/* Validation Status */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom color="primary">
                        Validation Status
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={2}>
                        <Box display="flex" alignItems="center">
                          {elfBinaryDetails.renodeBinaryPaths?.integrity_verified ? (
                            <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                          ) : (
                            <ErrorIcon sx={{ color: 'error.main', mr: 1 }} />
                          )}
                          <Typography>
                            Integrity Verified: {elfBinaryDetails.renodeBinaryPaths?.integrity_verified ? 'Yes' : 'No'}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                          {elfBinaryDetails.renodeBinaryPaths?.elf_validated ? (
                            <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                          ) : (
                            <ErrorIcon sx={{ color: 'error.main', mr: 1 }} />
                          )}
                          <Typography>
                            ELF Validated: {elfBinaryDetails.renodeBinaryPaths?.elf_validated ? 'Yes' : 'No'}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid> 
                </CardContent>
              </Card>
            ) : (
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom color="text.secondary">
                    No Binary file Uploaded
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Upload an binary file to start memory analysis with Renode.
                  </Typography>
                  <Button 
                    variant="contained" 
                    startIcon={<UploadFileIcon />}
                    onClick={() => setUploadDialogOpen(true)}
                  >
                    Upload Binary
                  </Button>
                </CardContent>
              </Card>
            )}
          </Paper>
        </Grid>
      )}

      {/* Rest of the Memory Analysis UI Components */}
      <Grid container spacing={3}>
        {/* Leak Summary */}
        <Grid item xs={12} md={activeTool === "renode" ? 6 : 6}>
          <Paper elevation={3} style={{ padding: "16px", height: "100%", minHeight: "200px" }}>
            <Typography variant="h6" gutterBottom>Memory Leak Summary</Typography>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell><strong>Definitely Lost</strong></TableCell>
                    <TableCell>
                      {formatBytes(leakSummary?.definitelyLostBytes)} ({leakSummary?.definitelyLostBlocks || "0"} blocks)
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Indirectly Lost</strong></TableCell>
                    <TableCell>
                      {formatBytes(leakSummary?.indirectlyLostBytes)} ({leakSummary?.indirectlyLostBlocks || "0"} blocks)
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Possibly Lost</strong></TableCell>
                    <TableCell>
                      {formatBytes(leakSummary?.possiblyLostBytes)} ({leakSummary?.possiblyLostBlocks || "0"} blocks)
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Heap Summary */}
        <Grid item xs={12} md={activeTool === "renode" ? 6 : 6}>
          <Paper elevation={3} style={{ padding: "16px", height: "100%", minHeight: "200px" }}>
            <Typography variant="h6" gutterBottom>Heap Memory Summary</Typography>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell><strong>Total Allocated</strong></TableCell>
                    <TableCell>{formatBytes(heapSummary?.totalBytesAllocated)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Total Freed</strong></TableCell>
                    <TableCell>{formatBytes(heapSummary?.totalBytesFreed)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>In-Use Bytes</strong></TableCell>
                    <TableCell>{formatBytes(heapSummary?.inUseBytes)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>In-Use Blocks</strong></TableCell>
                    <TableCell>{heapSummary?.inUseBlocks || "0"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Total Allocations</strong></TableCell>
                    <TableCell>{heapSummary?.totalAllocs || "0"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Total Frees</strong></TableCell>
                    <TableCell>{heapSummary?.totalFrees || "0"}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Renode Specific Sections */}
        {activeTool === "renode" && (
          <>
            {/* Static Analysis */}
            {staticAnalysis && (
              <Grid item xs={12} md={6}>
                <Paper elevation={3} style={{ padding: "16px", height: "100%", minHeight: "200px" }}>
                  <Typography variant="h6" gutterBottom>Static Analysis Summary</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell><strong>Total RAM</strong></TableCell>
                          <TableCell>{formatBytes(staticAnalysis.total_ram)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Total Flash</strong></TableCell>
                          <TableCell>{formatBytes(staticAnalysis.total_flash)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>RAM Usage</strong></TableCell>
                          <TableCell>
                            {staticAnalysis.ram_percent ? Number(staticAnalysis.ram_percent).toFixed(2) : "0.00"}%
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Flash Usage</strong></TableCell>
                          <TableCell>
                            {staticAnalysis.flash_percent ? Number(staticAnalysis.flash_percent).toFixed(2) : "0.00"}%
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            )}

            {/* Dynamic Analysis Summary */}
            {dynamicAnalysis && dynamicAnalysis.summary && (
              <Grid item xs={12} md={6}>
                <Paper elevation={3} style={{ padding: "16px", height: "100%", minHeight: "200px" }}>
                  <Typography variant="h6" gutterBottom>Dynamic Analysis Summary</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell><strong>Total Allocated</strong></TableCell>
                          <TableCell>{formatBytes(dynamicAnalysis.summary.total_allocated)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Total Freed</strong></TableCell>
                          <TableCell>{formatBytes(dynamicAnalysis.summary.total_freed)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Leaked Bytes</strong></TableCell>
                          <TableCell>{formatBytes(dynamicAnalysis.summary.leaked_bytes)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Leaked Blocks</strong></TableCell>
                          <TableCell>{dynamicAnalysis.summary.leaked_blocks || "0"}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Total Allocations</strong></TableCell>
                          <TableCell>{dynamicAnalysis.summary.total_allocations || "0"}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Total Frees</strong></TableCell>
                          <TableCell>{dynamicAnalysis.summary.total_frees || "0"}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            )}

            {/* Stack Usage */}
            {stackUsage !== null && (
              <Grid item xs={12} md={4}>
                <Paper elevation={3} style={{ padding: "16px", height: "100%", minHeight: "200px", textAlign: "center", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <Typography variant="h6" gutterBottom>Stack Usage</Typography>
                  <Typography variant="h4" color="primary">
                    {formatBytes(stackUsage)}
                  </Typography>
                </Paper>
              </Grid>
            )}

            {/* Unsafe Functions */}
            {unsafeFunctions && (
              <Grid item xs={12} md={8}>
                <Paper elevation={3} style={{ padding: "16px", height: "100%", minHeight: "200px" }}>
                  <Typography variant="h6" gutterBottom>
                    Unsafe Functions {unsafeFunctions.count > 0 && `(${unsafeFunctions.count})`}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', minHeight: '120px' }}>
                    {unsafeFunctions.count > 0 && unsafeFunctions.names ? (
                      unsafeFunctions.names.map((func, index) => (
                        <Chip 
                          key={index}
                          label={func} 
                          color="warning"
                          variant="outlined"
                          size="medium"
                          sx={{ fontSize: '0.9rem', padding: '4px 8px' }}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No unsafe functions detected
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* Dynamic Analysis Leaks Details */}
            {filesLeakage.length > 0 && (
              <Grid item xs={12}>
                <Paper elevation={3} style={{ padding: "16px" }}>
                  <Typography variant="h6" gutterBottom>Dynamic Analysis Leaks Details</Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>File Details</TableCell>
                          <TableCell>Total Bytes</TableCell>
                          <TableCell>Allocation Function</TableCell>
                          <TableCell> Description</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filesLeakage.map((leak, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              {leak.calleeDetails && leak.calleeDetails.map((callee, idx) => (
                                <div key={idx}>
                                  {callee.file}:{callee.line} - {callee.function}
                                </div>
                              ))}
                            </TableCell>
                            <TableCell>{formatBytes(leak.totalBytes)}</TableCell>
                            <TableCell>{leak.allocationFunction}</TableCell>
                            <TableCell>{leak.description}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            )}

            {/* All Runs Data with Pagination */}
            {allRunsData.length > 0 && (
              <Grid item xs={12}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">All Test Runs ({allRunsData.length})</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Run #</TableCell>
                            <TableCell>Completion Time</TableCell>
                            <TableCell>Leaked Bytes</TableCell>
                            <TableCell>Leaked Blocks</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {allRunsData
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((run) => (
                              <TableRow key={run.run_number}>
                                <TableCell>{run.run_number}</TableCell>
                                <TableCell>{new Date(run.completion_time).toLocaleString()}</TableCell>
                                <TableCell>{formatBytes(run.analysis_results?.dynamicAnalysis?.summary?.leaked_bytes)}</TableCell>
                                <TableCell>{run.analysis_results?.dynamicAnalysis?.summary?.leaked_blocks || "0"}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={run.status} 
                                    color={run.status === 'completed' ? 'success' : 'default'}
                                    size="small"
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      component="div"
                      count={allRunsData.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      sx={{
                        '.MuiTablePagination-toolbar': {
                          paddingLeft: 2,
                        }
                      }}
                    />
                  </AccordionDetails>
                </Accordion>
              </Grid>
            )}
          </>
        )}

        {/* File Leakage Details (Valgrind specific) */}
        {activeTool === "valgrind" && filesLeakage.length > 0 && (
          <Grid item xs={12}>
            <Paper elevation={3} style={{ padding: "16px", minHeight: "200px" }}>
              <Typography variant="h6">Memory Leak Details by File</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>File & Line</TableCell>
                      <TableCell>Line</TableCell>
                      <TableCell>Function</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Bytes Leaked</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filesLeakage.map((leak, index) =>
                      leak.calleeDetails.map((callee, idx) => (
                        <TableRow key={`${index}-${idx}`}>
                          <TableCell>{callee.file}</TableCell>
                          <TableCell>{callee.line}</TableCell>
                          <TableCell>{leak.allocationFunction}</TableCell>
                          <TableCell>{leak.description}</TableCell>
                          <TableCell>{leak.totalBytes} bytes</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* MCU Core Selection Dialog */}
      <Dialog open={mcuCoreDialogOpen} onClose={() => {
        setMcuCoreDialogOpen(false);
        setMcuCoreMessage({ type: '', text: '' });
      }} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            {/* <ArchitectureIcon sx={{ mr: 1, color: 'secondary.main' }} /> */}
            Select MCU Core
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Select the target MCU core for Dynamic Memeory analysis. This determines the architecture for memory analysis.
          </Typography>
          <FormControl fullWidth variant="outlined" sx={{ mt: 1 }}>
            <InputLabel>MCU Core</InputLabel>
            <Select
              value={mcuCore}
              onChange={handleMcuCoreChange}
              label="MCU Core"
            >
              {mcuCoreOptions.map((core) => (
                <MenuItem key={core} value={core}>
                  {core}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {mcuCoreMessage.text && (
            <Alert severity={mcuCoreMessage.type} sx={{ mt: 2 }}>
              {mcuCoreMessage.text}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setMcuCoreDialogOpen(false);
            setMcuCoreMessage({ type: '', text: '' });
          }} disabled={mcuCoreLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleMcuCoreUpdate} 
            variant="contained" 
            color="secondary"
            disabled={mcuCoreLoading || !mcuCore}
            // startIcon={mcuCoreLoading ? <CircularProgress size={16} /> : <ArchitectureIcon />}
          >
            {mcuCoreLoading ? 'Updating...' : 'Update MCU Core'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Run Count Dialog */}
      <Dialog open={dialogOpen} onClose={() => {
        setDialogOpen(false);
        setCustomRunCount('');
        setCustomInputValue('');
        setCustomError('');
        setUpdateMessage({ type: '', text: '' });
      }} maxWidth="sm" fullWidth>
        <DialogTitle>Update Run Count</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Update the number of test runs for Dynamic memory analysis. This will restart the analysis with the new configuration.
          </Typography>
          <FormControl fullWidth variant="outlined" sx={{ mt: 1 }}>
            <InputLabel>Select Run Count</InputLabel>
            <Select
              value={customRunCount && !runCountOptions.includes(parseInt(customRunCount)) ? "custom" : customRunCount}
              onChange={handleRunCountChange}
              label="Select Run Count"
            >
              {runCountOptions.map((count) => (
                <MenuItem key={count} value={count}>
                  {count} runs
                </MenuItem>
              ))}
              <MenuItem value="custom">
                <Box sx={{ width: '100%', py: 0.5 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
                    Custom (1-1000)
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    placeholder="Enter custom value"
                    value={customInputValue}
                    onChange={handleCustomInputChange}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    inputProps={{ 
                      min: 1, 
                      max: 1000,
                      step: 1
                    }}
                    error={!!customError}
                    helperText={customError}
                  />
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
          {updateMessage.text && (
            <Alert severity={updateMessage.type} sx={{ mt: 2 }}>
              {updateMessage.text}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDialogOpen(false);
            setCustomRunCount('');
            setCustomInputValue('');
            setCustomError('');
            setUpdateMessage({ type: '', text: '' });
          }} disabled={updateLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleRunCountUpdate} 
            variant="contained" 
            disabled={updateLoading || !customRunCount || !!customError}
            startIcon={updateLoading && <CircularProgress size={16} />}
          >
            {updateLoading ? 'Updating...' : 'Update Run Count'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => {
        setUploadDialogOpen(false);
        setSelectedFile(null);
        setUploadMessage({ type: '', text: '' });
        setUploadProgress(0);
      }} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Binary for Analysis</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Upload a compiled .elf binary file for Renode memory analysis. The file will be converted to base64 and sent to the backend for processing.
          </Typography>
          
          {!selectedFile ? (
            <Box sx={{ border: '2px dashed', borderColor: 'primary.main', borderRadius: 2, p: 3, textAlign: 'center' }}>
              <input
                accept=".elf"
                style={{ display: 'none' }}
                id="elf-file-upload"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="elf-file-upload">
                <Button variant="contained" component="span" startIcon={<UploadFileIcon />}>
                  Select Binary File
                </Button>
              </label>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Maximum file size: 100MB
              </Typography>
            </Box>
          ) : (
            <Box sx={{ p: 2, border: 1, borderColor: 'primary.main', borderRadius: 1 }}>
              <Grid container alignItems="center" spacing={2}>
                <Grid item>
                  <DescriptionIcon color="primary" />
                </Grid>
                <Grid item xs>
                  <Typography variant="body1">
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {formatFileSize(selectedFile.size)}
                  </Typography>
                </Grid>
                <Grid item>
                  <IconButton onClick={() => setSelectedFile(null)}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
              
              {uploadProgress > 0 && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={uploadProgress} 
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" align="center">
                    {uploadProgress}% {uploadLoading ? 'uploading...' : 'uploaded'}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          
          {uploadMessage.text && (
            <Alert severity={uploadMessage.type} sx={{ mt: 2 }}>
              {uploadMessage.text}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setUploadDialogOpen(false);
            setSelectedFile(null);
            setUploadMessage({ type: '', text: '' });
            setUploadProgress(0);
          }} disabled={uploadLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleFileUpload} 
            variant="contained" 
            disabled={uploadLoading || !selectedFile}
            startIcon={uploadLoading ? <CircularProgress size={16} /> : <UploadFileIcon />}
          >
            {uploadLoading ? 'Uploading...' : 'Upload File'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MemoryLeakDetails;